import { Card, CardHeader, Typography, CardContent, makeStyles, Box } from '@material-ui/core'
import Grid from '@material-ui/core/Grid'
import { Theme } from '@material-ui/core/styles/createTheme'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import { Button } from '@mui/material'
import classNames from 'classnames'
import { intersection } from 'lodash'
import upperFirst from 'lodash/upperFirst'
import React, { useMemo, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'

import api from 'src/api'
import { LightPolicy, DomainPermission_DomainEnum, DomainPermission_VerbEnum, PolicyAmount } from 'src/api/gen/api'
import useDeepCompareEffect from 'src/commons/hooks/useDeepCompareEffect'
import usePolicyKindsExceptFeeRates from 'src/commons/hooks/usePolicyKindsExceptFeeRates'
import { userHasPermission, Can } from 'src/commons/permissions'
import { ZonedDateTimeToJSON, TemporalNow } from 'src/commons/temporalHelpers'
import { CardFooter } from 'src/components/Card'
import { IDateTimeRange } from 'src/components/DatePickers'
import FormValidator from 'src/components/FormValidator'
import ValidationResults from 'src/components/FormValidator/ValidationResults'
import { useJoyrideWrapper } from 'src/components/Joyride'
import { Separator } from 'src/components/Separator'
import ErrorSnackbar from 'src/components/Snackbar'
import { useProviders } from 'src/layout/hooks'
import theme from 'src/theme'

import { IPenaltyScheme } from '..'

import { PeriodField, PoliciesField, ProviderField } from './fields'

interface IPublishButtonProps extends Omit<React.ComponentProps<typeof Button>, 'variant' | 'color' | 'type'> {
  label?: string
}

export const PublishButton = function (props: IPublishButtonProps) {
  const { t } = useTranslation()

  const { label, ...rest } = props

  return (
    <Button
      variant={'contained'}
      color={'primary'}
      sx={(theme) => ({
        margin: theme.spacing(),
        background: theme.palette.success.main,
        '&:hover': { backgroundColor: theme.palette.success.dark },
      })}
      {...rest}
    >
      {label ?? t('button.publish')}
    </Button>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  cardLoading: {
    padding: theme.spacing(80),
  },
  root: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
    borderRadius: 'inherit',
  },
  rootCard: {
    borderRadius: 'inherit',
    boxShadow: theme.shadows[1],
  },
  cardSection: {
    borderRadius: 'inherit',
    display: 'flex',
    flexWrap: 'wrap',
    paddingLeft: '30px',
    marginBottom: '30px',
  },
  cardHeaderTitle: {
    color: theme.palette.primary.main,
    margin: '15px 0px 15px 15px',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '15px 0px 15px',
  },
  sectionHeader: {
    width: '100%',
    marginBottom: '-6px',
  },
  buttonBar: { paddingRight: '32px', paddingLeft: '32px', textAlign: 'end' },
  closeNotifButton: {
    padding: theme.spacing(1 / 2),
  },
}))

export const formRules = {
  providers: {
    method: (value: string[]) => !!value.length,
    message: <Trans>messages.providers</Trans>,
  },
  periodFrom: {
    source: 'startDate',
    method: (value: Temporal.ZonedDateTime | undefined) => !!value,
    message: <Trans>messages.startDate</Trans>,
  },
  periodTo: {
    source: 'endDate',
    method: (value: Temporal.ZonedDateTime | undefined) => !!value,
    message: <Trans>messages.endDate</Trans>,
  },
  policies: {
    method: (value: string[]) => !!value.length,
    message: <Trans>messages.policies</Trans>,
  },
}
const validator = new FormValidator<typeof formRules>(formRules)

const IPenaltySchemeToCreatePenaltyScheme = (penaltyScheme: IPenaltySchemeSettings) => ({
  start_date: ZonedDateTimeToJSON(penaltyScheme.startDate ? penaltyScheme.startDate : TemporalNow()),
  end_date: ZonedDateTimeToJSON(penaltyScheme.endDate ? penaltyScheme.endDate : TemporalNow()),
  policies: penaltyScheme.policies,
  providers: penaltyScheme.providers,
  is_fee_rates: false,
})

interface ICardSection extends React.ComponentProps<typeof Grid> {
  button?: React.ReactNode
  children: React.ReactNode
  itemWrapping?: boolean
  title?: string
}
export const CardSection = function (props: ICardSection) {
  const classes = useStyles()
  const { spacing = 4, itemWrapping = true, xs, sm, md, lg, xl } = props

  const breakpoints = { xs, sm, md, lg, xl }

  return (
    <CardContent className={classes.cardSection}>
      <Grid container spacing={spacing}>
        <Grid item xs={12} style={{ display: 'flex' }}>
          <Typography className={classNames(classes.cardHeaderTitle, classes.sectionHeader)} variant="h5">
            {props.title}
          </Typography>
          {props.button}
        </Grid>
        {itemWrapping
          ? React.Children.map(props.children, (child) => (
              <Grid item {...breakpoints}>
                {child}
              </Grid>
            ))
          : props.children}
      </Grid>
    </CardContent>
  )
}

interface IPenaltySchemeSettings {
  endDate: Temporal.ZonedDateTime | undefined
  policies: string[]
  providers: string[]
  startDate: Temporal.ZonedDateTime | undefined
}

interface IPenaltySchemeEditorProps {
  setPenaltyScheme: React.Dispatch<React.SetStateAction<IPenaltyScheme | null | undefined>>
  setPolicyAmounts: React.Dispatch<React.SetStateAction<Map<string, PolicyAmount[]>>>
}

export const filterPoliciesByProviders = function (policies: LightPolicy[], providers: string[]) {
  return policies.filter((policy) => providers.length === 0 || intersection(policy.providers, providers).length !== 0)
}

const PenaltySchemeEditor = (props: IPenaltySchemeEditorProps) => {
  const classes = useStyles(theme)
  const { t } = useTranslation()
  const policyKinds = usePolicyKindsExceptFeeRates()
  const { activeProviders } = useProviders()

  const { run } = useJoyrideWrapper()

  const canWrite = userHasPermission([DomainPermission_VerbEnum.write, DomainPermission_DomainEnum.policy])

  const [notificationOpened, setNotificationOpened] = useState<boolean>(false)
  const [notificationMessage, setNotificationMessage] = useState<string>('')

  // Wording context
  const [penaltyScheme, setPenaltyScheme] = useState<IPenaltySchemeSettings>({
    startDate: undefined,
    endDate: undefined,
    policies: [],
    providers: [],
  })

  const [validation, setValidation] = useState<ValidationResults<typeof formRules>>(validator.makeValid())

  const [allPolicies, setAllPolicies] = useState<LightPolicy[]>([])

  async function fetchPolicies() {
    const response = await api.policies.policiesListLight({ kind: policyKinds.map((kind) => kind.id), published: true })
    const policies = response.results
    setAllPolicies(policies)
  }

  useDeepCompareEffect(() => {
    fetchPolicies()
  }, [policyKinds])

  const availablePolicies = useMemo(
    () => filterPoliciesByProviders(allPolicies, penaltyScheme.providers),
    [allPolicies, penaltyScheme.providers]
  )

  const penaltySchemePolicies = useMemo(
    () => allPolicies.filter((policy) => penaltyScheme.policies.includes(policy.id)),
    [allPolicies, penaltyScheme.policies]
  )

  async function validateAndCreate() {
    const formValidation = validator.validate(penaltyScheme)
    setValidation(formValidation)

    if (!formValidation.isFormValid()) {
      setNotificationMessage(t('penaltyScheme.missingFields'))
      setNotificationOpened(true)
      return
    }
    try {
      setPenaltyScheme({ ...penaltyScheme })
      const createdPenaltyScheme = await api.penaltySchemes.penaltySchemesCreate({
        data: IPenaltySchemeToCreatePenaltyScheme(penaltyScheme),
      })
      props.setPenaltyScheme({ ...createdPenaltyScheme })

      /**
       * This strange construction is necessary to allow a smooth compute on create by querying the PolicyAmount create
       * API one at a time and appending the result to our displayed penaltyScheme.
       * The result should be cascading numbers.
       * It is necessary only at creation time since computed objects are saved in the database
       */
      props.setPolicyAmounts(new Map(createdPenaltyScheme.providers.map((provider_id) => [provider_id, []])))
      /**
       * `penaltySchemePolicies` is actually the same as `createdPenaltyScheme.policies_names` but using it allows us
       * to ask creation of pair of policy/provider only when the provider is actually part of the policy
       */
      for (const { id: policy_id, providers } of penaltySchemePolicies) {
        for (const provider_id of providers ?? []) {
          // We do NOT want to make this queries abortable as they need to be computed for the next retrieve
          api.policyAmounts
            .policyAmountsCreate({
              penalty_scheme: createdPenaltyScheme.id,
              data: {
                policy: policy_id,
                provider: provider_id,
              },
            })
            .then((policyAmount) => {
              props.setPolicyAmounts((pendingPolicyAmounts) => {
                pendingPolicyAmounts.get(provider_id)!.push(policyAmount)
                return new Map(pendingPolicyAmounts)
              })
            })
        }
      }
    } catch (e) {
      setNotificationMessage(t('penaltyScheme.serverError'))
      setNotificationOpened(true)
    }
  }

  const handleProvidersChange = function (providers: string[]) {
    setPenaltyScheme({
      ...penaltyScheme,
      providers,
      policies: filterPoliciesByProviders(penaltySchemePolicies, providers).map((policy) => policy.id),
    })
  }

  function handlePoliciesChange(policies: string[]) {
    setPenaltyScheme({ ...penaltyScheme, policies })
  }

  function handleDateRangeChange(dateRange: IDateTimeRange) {
    penaltyScheme.startDate = dateRange.from
    penaltyScheme.endDate = dateRange.to
    setPenaltyScheme({
      ...penaltyScheme,
    })
  }

  return (
    <div className={classes.root}>
      <Card className={classes.rootCard}>
        <CardHeader
          title={
            <Box display="flex">
              <Box display="flex" flexGrow={1}>
                <Typography className={classes.cardHeaderTitle} variant="h2">
                  {upperFirst(t('titles.penalty_scheme.editor.penalties'))}
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                <Button variant="outlined" color="primary" startIcon={<HelpOutlineIcon />} onClick={() => run()}>
                  {t('onboarding.ui.start_button')}
                </Button>
              </Box>
            </Box>
          }
        />
        <Separator />
        <CardSection xs={4}>
          <ProviderField
            id="penalty_scheme_provider"
            title={t('titles.providers')}
            providers={activeProviders}
            value={penaltyScheme.providers}
            handleChange={handleProvidersChange}
            validation={validation}
          />
          <PoliciesField
            id="penalty_scheme_policies"
            title={t('titles.policies')}
            value={penaltyScheme.policies}
            allPolicies={availablePolicies}
            handleChange={handlePoliciesChange}
            validation={validation}
          />
          <PeriodField
            id="penalty_scheme_time_range"
            title={t('titles.time_range.penalty_scheme')}
            value={{ from: penaltyScheme.startDate, to: penaltyScheme.endDate }}
            handleChange={handleDateRangeChange}
            validation={validation}
          />
        </CardSection>
        <Can I={DomainPermission_VerbEnum.write} a={DomainPermission_DomainEnum.policy}>
          <Separator />
          <CardFooter className={classes.cardFooter}>
            <div className={classes.buttonBar}>
              {canWrite ? <PublishButton label={t('titles.generate')} onClick={validateAndCreate} /> : null}
            </div>
          </CardFooter>
        </Can>
      </Card>

      <ErrorSnackbar
        open={notificationOpened}
        onClose={() => setNotificationOpened(false)}
        message={notificationMessage}
      />
    </div>
  )
}

export default PenaltySchemeEditor
